import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';

const Rankings = ({
  API,
  getAuthHeaders,
  setCurrentView,
  rankingAlunos,
  setRankingAlunos,
  rankingProfessores,
  setRankingProfessores,
  rankingTurmas,
  setRankingTurmas,
  activeTab,
  setActiveTab
}) => {
  const [isLoadingRankings, setIsLoadingRankings] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('mes'); // 'mes', 'trimestre', 'ano'
  const [filteredRankingAlunos, setFilteredRankingAlunos] = useState({ ranking: [] });
  const [filteredRankingProfessores, setFilteredRankingProfessores] = useState({ ranking: [] });
  const [filteredRankingTurmas, setFilteredRankingTurmas] = useState({ ranking: [] });

  // Função para calcular ranking denso
  const calculateDenseRanking = (ranking) => {
    if (!ranking || ranking.length === 0) return [];

    let currentRank = 1;
    let previousScore = null;

    return ranking.map((item, index) => {
      if (previousScore !== null && item.total_presencas !== previousScore) {
        currentRank = index + 1;
      }
      previousScore = item.total_presencas;

      return {
        ...item,
        posicao_densa: currentRank
      };
    });
  };

  // Função para filtrar por período usando dados reais
  const filterByPeriod = async (ranking, period) => {
    if (!ranking || ranking.length === 0) return [];

    try {
      // Buscar todos os registros de presença para recalcular baseado no período
      const response = await axios.get(`${API}/presencas/todas`, { headers: getAuthHeaders() });
      const todasPresencas = response.data;

      const filteredRanking = [];

      for (const aluno of ranking) {
        // Filtrar presenças do aluno por período
        const presencasAluno = todasPresencas.filter(presenca => {
          if (presenca.aluno_id !== aluno.aluno_id) return false;

          const dataPresenca = presenca.data;

          switch (period) {
            case 'mes':
              return moment(dataPresenca).isSame(moment(), 'month');
            case 'trimestre':
              return moment(dataPresenca).isBetween(moment().startOf('quarter'), moment().endOf('quarter'), null, '[]');
            case 'ano':
              return moment(dataPresenca).isSame(moment(), 'year');
            default:
              return true;
          }
        });

        // Calcular Dom. (dias únicos) e Pres. (total de presenças)
        const diasUnicos = new Set(presencasAluno.map(p => p.data)).size;
        const totalPresencas = presencasAluno.length;

        // Calcular percentual real (Pres./Dom. × 100)
        const percentual = diasUnicos > 0 ? (totalPresencas / diasUnicos * 100) : 0;

        filteredRanking.push({
          ...aluno,
          total_presencas: totalPresencas,
          domingos_presentes: diasUnicos,
          percentual_frequencia: Math.round(percentual * 10) / 10 // 1 casa decimal
        });
      }

      // Ordenar por total de presenças (desc)
      return filteredRanking.sort((a, b) => b.total_presencas - a.total_presencas);

    } catch (error) {
      // Em caso de erro, retorna o ranking original
      return ranking;
    }
  };

  // Função específica para filtrar turmas por período
  const filterTurmasByPeriod = async (turmas, period) => {
    if (!turmas || turmas.length === 0) return [];

    try {
      // Buscar todas as presenças
      const response = await axios.get(`${API}/presencas/todas`, { headers: getAuthHeaders() });
      const todasPresencas = response.data;

      const filteredTurmas = [];

      for (const turma of turmas) {
        // Filtrar presenças da turma por período
        const presencasTurma = todasPresencas.filter(presenca => {
          if (presenca.turma_id !== turma.turma_id) return false;

          const dataPresenca = presenca.data;

          switch (period) {
            case 'mes':
              return moment(dataPresenca).isSame(moment(), 'month');
            case 'trimestre':
              return moment(dataPresenca).isBetween(moment().startOf('quarter'), moment().endOf('quarter'), null, '[]');
            case 'ano':
              return moment(dataPresenca).isSame(moment(), 'year');
            default:
              return true;
          }
        });

        // Calcular Dom. (dias únicos com presença)
        const domingosComPresenca = new Set(presencasTurma.map(p => p.data));
        const totalDomingos = domingosComPresenca.size || 1;

        // Calcular total de presenças
        const totalPresencas = presencasTurma.length;

        // Calcular média de presenças por domingo
        const mediaPresencas = totalPresencas / totalDomingos;

        // Calcular percentual de frequência (média / matriculados * 100)
        const percentualFrequencia = turma.matriculados > 0 ? (mediaPresencas / turma.matriculados * 100) : 0;

        filteredTurmas.push({
          ...turma,
          total_presencas: totalPresencas,
          domingos_com_dados: totalDomingos,
          media_presencas: Math.round(mediaPresencas * 10) / 10,
          percentual_frequencia: Math.round(percentualFrequencia * 10) / 10
        });
      }

      // Ordenar por percentual de frequência (desc)
      return filteredTurmas.sort((a, b) => b.percentual_frequencia - a.percentual_frequencia);

    } catch (error) {
      return turmas;
    }
  };

  useEffect(() => {
    // Só carrega se ainda não tiver dados
    if (!rankingAlunos.ranking || !rankingProfessores.ranking || !rankingTurmas.ranking) {
      loadRankingsData();
    }
  }, []);

  // Aplicar filtros quando dados ou período mudam
  useEffect(() => {
    const applyFilters = async () => {
      if (rankingAlunos.ranking) {
        const filtered = await filterByPeriod(rankingAlunos.ranking, selectedPeriod);
        const withDenseRanking = calculateDenseRanking(filtered);
        setFilteredRankingAlunos({ ...rankingAlunos, ranking: withDenseRanking });
      }

      if (rankingProfessores.ranking) {
        const filtered = await filterByPeriod(rankingProfessores.ranking, selectedPeriod);
        const withDenseRanking = calculateDenseRanking(filtered);
        setFilteredRankingProfessores({ ...rankingProfessores, ranking: withDenseRanking });
      }

      if (rankingTurmas.ranking) {
        const filtered = await filterTurmasByPeriod(rankingTurmas.ranking, selectedPeriod);
        setFilteredRankingTurmas({ ...rankingTurmas, ranking: filtered });
      }
    };

    applyFilters();
  }, [rankingAlunos, rankingProfessores, rankingTurmas, selectedPeriod]);

  const loadRankingsData = async () => {
    if (isLoadingRankings) return; // Evita múltiplas chamadas simultâneas

    setIsLoadingRankings(true);
    try {
      const [alunosResponse, professoresResponse, turmasResponse] = await Promise.all([
        axios.get(`${API}/ranking/alunos`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ranking/professores-oficiais`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ranking/turmas`, { headers: getAuthHeaders() })
      ]);

      setRankingAlunos(alunosResponse.data);
      setRankingProfessores(professoresResponse.data);
      setRankingTurmas(turmasResponse.data);
    } catch (error) {
      alert('Erro ao carregar rankings. Tente novamente.');
    } finally {
      setIsLoadingRankings(false);
    }
  };

  const handleTabChange = (newTab) => {
    // Debounce para evitar mudanças rápidas de aba
    if (isLoadingRankings) return;
    setActiveTab(newTab);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            ← Voltar ao Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">🏆 Rankings</h1>
          <p className="text-gray-600">Ranking de presença e desempenho</p>
        </div>

        {/* Filtros de Período */}
        <div className="mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">📅 Filtro por Período</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedPeriod('mes')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedPeriod === 'mes'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📅 Mês Atual
              </button>
              <button
                onClick={() => setSelectedPeriod('trimestre')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedPeriod === 'trimestre'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📊 Trimestre
              </button>
              <button
                onClick={() => setSelectedPeriod('ano')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedPeriod === 'ano'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🗓️ Ano
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {selectedPeriod === 'mes' && `📊 Exibindo dados do mês de ${new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })} (~30% do período total)`}
              {selectedPeriod === 'trimestre' && `📊 Exibindo dados do ${Math.floor(new Date().getMonth() / 3) + 1}º trimestre de ${new Date().getFullYear()} (~75% do período total)`}
              {selectedPeriod === 'ano' && `📊 Exibindo dados completos do ano de ${new Date().getFullYear()} (100% do período)`}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-1 bg-gray-200 p-2 sm:p-1 rounded-lg">
            <button
              onClick={() => handleTabChange('alunos')}
              disabled={isLoadingRankings}
              className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                activeTab === 'alunos'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-300'
              }`}
            >
              🎓 Alunos Gerais
            </button>
            <button
              onClick={() => handleTabChange('professores')}
              disabled={isLoadingRankings}
              className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                activeTab === 'professores'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-300'
              }`}
            >
              👨‍🏫 Professores e Oficiais
            </button>
            <button
              onClick={() => handleTabChange('turmas')}
              disabled={isLoadingRankings}
              className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                activeTab === 'turmas'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-300'
              }`}
            >
              🏫 Turmas
            </button>
          </div>

          {/* Botão de refresh */}
          <div className="mt-3 text-right">
            <button
              onClick={loadRankingsData}
              disabled={isLoadingRankings}
              className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ml-auto"
            >
              {isLoadingRankings ? (
                <>
                  <span className="animate-spin">⟳</span>
                  Carregando...
                </>
              ) : (
                <>
                  🔄 Atualizar Rankings
                </>
              )}
            </button>
          </div>
        </div>

        {/* Conteúdo dos Rankings */}
        <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
          {isLoadingRankings ? (
            <div className="text-center py-12">
              <div className="animate-spin text-4xl mb-4">⟳</div>
              <p className="text-gray-600">Carregando rankings...</p>
            </div>
          ) : (
            <>
              {activeTab === 'alunos' && (
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">Ranking Geral de Alunos</h2>
                  <p className="text-sm md:text-base text-gray-600 mb-6">Top 150 alunos com mais presenças</p>

                  {filteredRankingAlunos.ranking && filteredRankingAlunos.ranking.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 text-xs md:text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Pos.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-left">Nome</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-left">Turma</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Pres.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Dom.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRankingAlunos.ranking.map((aluno, index) => (
                              <tr key={`aluno-${aluno.aluno_id}-${index}`} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center font-bold">
                                  {aluno.posicao_densa === 1 && '🥇'}
                                  {aluno.posicao_densa === 2 && '🥈'}
                                  {aluno.posicao_densa === 3 && '🥉'}
                                  {aluno.posicao_densa > 3 && `${aluno.posicao_densa}º`}
                                </td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 font-medium">{aluno.nome}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2">{aluno.turma}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{aluno.total_presencas}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{aluno.domingos_presentes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 text-xs md:text-sm text-gray-600">
                        <p>Total de alunos no ranking: {filteredRankingAlunos.total_alunos || filteredRankingAlunos.ranking.length}</p>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>📊 Nenhum dado de ranking disponível</p>
                      <p className="text-sm mt-2">Tente atualizar os rankings</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'professores' && (
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">Ranking Professores e Oficiais</h2>
                  <p className="text-sm md:text-base text-gray-600 mb-6">Ranking da turma de liderança da igreja</p>

                  {filteredRankingProfessores.ranking && filteredRankingProfessores.ranking.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 text-xs md:text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Pos.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-left">Nome</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Pres.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Dom.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRankingProfessores.ranking.map((professor, index) => (
                              <tr key={`prof-${professor.aluno_id}-${index}`} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center font-bold">
                                  {professor.posicao_densa === 1 && '🥇'}
                                  {professor.posicao_densa === 2 && '🥈'}
                                  {professor.posicao_densa === 3 && '🥉'}
                                  {professor.posicao_densa > 3 && `${professor.posicao_densa}º`}
                                </td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 font-medium">{professor.nome}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{professor.total_presencas}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{professor.domingos_presentes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 text-xs md:text-sm text-gray-600">
                        <p>Total de membros: {filteredRankingProfessores.total_membros || filteredRankingProfessores.ranking.length}</p>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>📊 Nenhum dado de ranking disponível</p>
                      <p className="text-sm mt-2">Tente atualizar os rankings</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'turmas' && (
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">Ranking de Turmas</h2>
                  <p className="text-sm md:text-base text-gray-600 mb-6">Turmas ordenadas por frequência média</p>

                  {filteredRankingTurmas.ranking && filteredRankingTurmas.ranking.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 text-xs md:text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Pos.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-left">Turma</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Matric.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Média</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Freq.</th>
                              <th className="border border-gray-300 px-2 md:px-3 py-2 text-center">Dom.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRankingTurmas.ranking.map((turma, index) => (
                              <tr key={`turma-${turma.turma_id}-${index}`} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center font-bold">
                                  {index + 1 === 1 && '🥇'}
                                  {index + 1 === 2 && '🥈'}
                                  {index + 1 === 3 && '🥉'}
                                  {index + 1 > 3 && `${index + 1}º`}
                                </td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 font-medium">{turma.turma_nome}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{turma.matriculados}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{turma.media_presencas}</td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center font-semibold text-green-600">
                                  {turma.percentual_frequencia}%
                                </td>
                                <td className="border border-gray-300 px-2 md:px-3 py-2 text-center">{turma.domingos_com_dados}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 text-xs md:text-sm text-gray-600">
                        <p>Total de turmas: {filteredRankingTurmas.total_turmas || filteredRankingTurmas.ranking.length}</p>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>📊 Nenhum dado de ranking disponível</p>
                      <p className="text-sm mt-2">Tente atualizar os rankings</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2 text-sm md:text-base">Como funciona o ranking:</h4>
            <ul className="text-xs md:text-sm text-blue-700 space-y-1">
              <li>• <strong>Alunos:</strong> Ordenados por total de presenças (domingos presentes)</li>
              <li>• <strong>Professores:</strong> Ranking específico da turma de liderança</li>
              <li>• <strong>Turmas:</strong> Ordenadas por percentual de frequência média</li>
              <li>• <strong>Cálculo:</strong> Baseado nos dados reais de chamada registrados</li>
              <li>• <strong>Ranking Denso:</strong> Alunos com mesma pontuação ficam na mesma posição</li>
              <li>• <strong>Filtros:</strong> Use os botões de período para ver rankings específicos por mês, trimestre ou ano</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rankings;
